import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { getRackModel } from "../../../modelPaths"
import { CaveOptions } from "../../../RackTypes"
import { PANEL_ONLY_OFFSET_90, PANEL_ONLY_OFFSET_100 } from "../utils/caveConstants"

export class CaveShelfBuilder {
  protected config: CaveOptions
  protected material: MaterialInfo

  constructor(config: CaveOptions, material: MaterialInfo) {
    this.config = config
    this.material = material
  }

  private getPanelWidth(left: boolean): string {
    return left ? this.config.leftPanelWidth : this.config.rightPanelWidth
  }

  createHardware(left: boolean): PartDefinition {
    return {
      mesh      : getRackModel('CAVE_SHELVES', `hardware ${this.getPanelWidth(left)}`),
      slots     : {},
      materials : this.material,
    }
  }

  createRMShelfBolt(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel('CAVE_SHELVES', 'rm bolt'),
      slots       : {},
      materials   : this.material,
      anchor      : left ? 'left' : 'right',
      offset      : left ? { x: 0.2 } : { x: -0.2 },
      adjustments : left ? [{ type: 'flip', axis: 'X' }] : [],
    }
  }

  addKettleBellShelf(left: boolean): PartDefinition {
    return {
      mesh      : getRackModel('CAVE_SHELVES', `kb ${this.getPanelWidth(left)}`),
      slots     : {},
      materials : this.material,
    }
  }

  addDumbBellShelf(left: boolean): PartDefinition {
    return {
      mesh      : getRackModel('CAVE_SHELVES', `db ${this.getPanelWidth(left)}`),
      slots     : {},
      materials : this.material,
    }
  }

  addKettleBellLiner(left: boolean): PartDefinition {
    return {
      mesh      : getRackModel('CAVE_SHELVES', `kb liner ${this.getPanelWidth(left)}`),
      slots     : {},
      materials : this.material,
    }
  }

  // MARK: Medball
  createMedballShelf(left: boolean, shelfSpacingOffset: number): PartDefinition {
    return {
      mesh  : getRackModel('CAVE_SHELVES', `medball ${this.getPanelWidth(left)}`),
      slots : {
        bolts: this.createHardware(left),
      },
      anchor    : 'right',
      materials : this.material,
      offset    : { y: shelfSpacingOffset },
    }
  }

  // MARK: KB
  createKettlebellShelf(shelfName:string | undefined, left: boolean, shelfSpacingOffset: number): PartDefinition {
    const isLinerSelected = !!shelfName?.includes('liner')

    return {
      mesh  : getRackModel('CAVE_SHELVES', `medball ${this.getPanelWidth(left)}`),
      slots : {
        bolts : this.createHardware(left),
        kb    : this.addKettleBellShelf(left), // kb shelf fits on top of the medball shelf
        liner : isLinerSelected ? this.addKettleBellLiner(left) : [], // kb liner stacks on top of the kb shelf
      },
      anchor      : left ? 'left' : 'right',
      materials   : this.material,
      offset      : { y: shelfSpacingOffset },
      adjustments : left ? [{ type: 'flip', axis: 'X' }] : [],
    }
  }

  // MARK: Bumper
  createBumperShelf(left: boolean, shelfSpacingOffset: number): PartDefinition {
    return {
      mesh  : getRackModel('CAVE_SHELVES', `bumper ${this.getPanelWidth(left)}`),
      slots : {
        bolts: this.createHardware(left),
      },
      anchor    : 'right',
      materials : this.material,
      offset    : { y: shelfSpacingOffset },
    }
  }

  // MARK: DB
  createDumbbellShelf(left: boolean, shelfSpacingOffset: number): PartDefinition {
    return {
      mesh  : getRackModel('CAVE_SHELVES', `medball ${this.getPanelWidth(left)}`),
      slots : {
        bolts : this.createHardware(left),
        db    : this.addDumbBellShelf(left), // db shelf fits on top of the medball shelf
      },
      anchor      : left ? 'left' : 'right',
      materials   : this.material,
      offset      : { y: shelfSpacingOffset },
      adjustments : left ? [{ type: 'flip', axis: 'X' }] : [],
    }
  }

  // MARK: Storage Panel
  createStoragePanel(left: boolean, shelfSpacingOffset: number, isPanelOnlyConfig: boolean | undefined): PartDefinition {
    let extraPanelOffset = 0

    if (isPanelOnlyConfig && this.config.height === '90') {
      extraPanelOffset = PANEL_ONLY_OFFSET_90 
    } else if (isPanelOnlyConfig && this.config.height === '100') {
      extraPanelOffset = PANEL_ONLY_OFFSET_100 
    }

    return {
      mesh  : getRackModel('CAVE_SHELVES', `panel ${this.getPanelWidth(left)}`),
      slots : {
        bolt1 : this.createRMShelfBolt(false),
        bolt2 : this.createRMShelfBolt(false),
        bolt3 : this.createRMShelfBolt(true),
        bolt4 : this.createRMShelfBolt(true),
      },
      anchor      : left ? 'left' : 'right',
      adjustments : left 
        ? [{ type: 'flip', axis: 'X' }, { type: 'rotate', axis: 'Y', amount: 180 }] 
        : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      offset    : left ? { x: -0.3, y: shelfSpacingOffset + extraPanelOffset + -0.5 } : { x: -0.3, y: shelfSpacingOffset + extraPanelOffset + -0.5 },
      materials : this.material,
    }
  }

  shelfFactory(shelfName: string | undefined, left: boolean, shelfSpacingOffset: number, isPanelOnlyConfig?: boolean | undefined): PartDefinition | undefined {
    switch (shelfName) {
      case 'medball shelf':
        return this.createMedballShelf(left, shelfSpacingOffset)

      case 'kettlebell shelf':
      case 'liner':
        return this.createKettlebellShelf(shelfName, left, shelfSpacingOffset)

      case 'bumper':
        return this.createBumperShelf(left, shelfSpacingOffset)

      case 'dumbbell shelf':
        return this.createDumbbellShelf(left, shelfSpacingOffset)

      case 'panel':
        return this.createStoragePanel(left, shelfSpacingOffset, isPanelOnlyConfig)

      case undefined:
        return undefined

      default:
        return this.createMedballShelf(left, shelfSpacingOffset)
    }
  }

  createShelf(shelfname: string | undefined, left: boolean, shelfSpacingOffset: number = 0, isPanelOnlyConfig?: boolean | undefined) {
    return this.shelfFactory(shelfname, left, shelfSpacingOffset, isPanelOnlyConfig)
  }
}